import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  adminListProducts,
  adminAdjustInventory,
  adminInventoryLogs,
  adminLowStock,
  adminOutOfStock,
} from "../../services/api";

const label = "text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal";
const input = "mt-2 w-full border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-orange";

const TABS = [
  { key: "adjust", label: "Adjust stock" },
  { key: "low", label: "Low stock" },
  { key: "out", label: "Out of stock" },
  { key: "logs", label: "History" },
];

export default function AdminInventory() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("adjust");
  const [products, setProducts] = useState([]);
  const [low, setLow] = useState([]);
  const [out, setOut] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ slug: "", type: "stock_in", quantity: "", reason: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    adminListProducts({ limit: 100, sort: "name" }).then((res) => { const p = res.items; setProducts(p); setForm((f) => ({ ...f, slug: f.slug || p[0]?.slug || "" })); }).catch(() => {});
    adminLowStock().then(setLow).catch(() => {});
    adminOutOfStock().then(setOut).catch(() => {});
    adminInventoryLogs().then(setLogs).catch(() => {});
  };
  useEffect(() => { if (ready && user?.role === "admin") refresh(); }, [ready, user]);

  const current = useMemo(() => products.find((p) => p.slug === form.slug), [products, form.slug]);

  const submit = async () => {
    if (!form.slug) return toast.error("Select a product.");
    if (form.quantity === "") return toast.error("Enter a quantity.");
    setSaving(true);
    try {
      const res = await adminAdjustInventory({
        slug: form.slug,
        type: form.type,
        quantity: Number(form.quantity),
        reason: form.reason || undefined,
        notes: form.notes || undefined,
      });
      toast.success(`Stock updated → ${res.stock}`);
      setForm((f) => ({ ...f, quantity: "", reason: "", notes: "" }));
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed."); }
    finally { setSaving(false); }
  };

  const list = (rows, testid) => (
    <div className="overflow-x-auto border border-brand-sand bg-brand-surface" data-testid={testid}>
      <table className="w-full min-w-[520px] text-left text-sm">
        <thead><tr className="border-b border-brand-sand text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-stone">
          <th className="p-3">Product</th><th className="p-3">SKU</th><th className="p-3">Stock</th><th className="p-3">Alert</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={4} className="p-6 text-center text-brand-stone">Nothing here.</td></tr> :
            rows.map((p) => (
              <tr key={p.slug} className="border-b border-brand-sand/50" data-testid={`inv-row-${p.slug}`}>
                <td className="p-3 font-medium text-brand-charcoal">{p.name}</td>
                <td className="p-3 text-brand-stone">{p.sku || "—"}</td>
                <td className="p-3 text-brand-charcoal">{p.stock ?? "—"}</td>
                <td className="p-3 text-brand-stone">{p.low_stock_alert ?? 5}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <AdminLayout title="Inventory" subtitle="Stock in / out, adjustments & history">
      <div data-testid="admin-inventory-page">
        <div className="flex flex-wrap gap-2 border-b border-brand-sand">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`inv-tab-${t.key}`}
              className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${tab === t.key ? "border-b-2 border-brand-orange text-brand-orange" : "text-brand-stone hover:text-brand-charcoal"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "adjust" && (
            <div className="max-w-xl border border-brand-sand bg-brand-surface p-6" data-testid="inv-adjust-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="i-product">Product</label>
                  <select id="i-product" data-testid="inv-product" className={input} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}>
                    {products.map((p) => <option key={p.slug} value={p.slug}>{p.name} (stock {p.stock ?? 0})</option>)}
                  </select>
                  {current && <p className="mt-2 text-xs text-brand-stone" data-testid="inv-current-stock">Current stock: <strong>{current.stock ?? 0}</strong></p>}
                </div>
                <div>
                  <label className={label} htmlFor="i-type">Action</label>
                  <select id="i-type" data-testid="inv-type" className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="stock_in">Stock in (+)</option>
                    <option value="stock_out">Stock out (−)</option>
                    <option value="adjustment">Set exact quantity</option>
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="i-qty">Quantity</label>
                  <input id="i-qty" data-testid="inv-quantity" type="number" min="0" className={input} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="i-reason">Reason</label>
                  <input id="i-reason" data-testid="inv-reason" className={input} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. new stock, damaged, correction" />
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="i-notes">Notes</label>
                  <textarea id="i-notes" data-testid="inv-notes" rows={2} className={input} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <button onClick={submit} disabled={saving} data-testid="inv-submit" className="mt-6 bg-brand-charcoal px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50">
                {saving ? "Updating…" : "Apply adjustment"}
              </button>
            </div>
          )}

          {tab === "low" && list(low, "inv-low-list")}
          {tab === "out" && list(out, "inv-out-list")}

          {tab === "logs" && (
            <div className="overflow-x-auto border border-brand-sand bg-brand-surface" data-testid="inv-logs">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead><tr className="border-b border-brand-sand text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-stone">
                  <th className="p-3">Date</th><th className="p-3">Product</th><th className="p-3">Type</th><th className="p-3">Change</th><th className="p-3">Result</th><th className="p-3">Reason</th>
                </tr></thead>
                <tbody>
                  {logs.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-brand-stone">No history yet.</td></tr> :
                    logs.map((l) => (
                      <tr key={l.id} className="border-b border-brand-sand/50" data-testid={`log-${l.id}`}>
                        <td className="p-3 text-brand-stone">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                        <td className="p-3 font-medium text-brand-charcoal">{l.product_name}</td>
                        <td className="p-3 text-brand-stone">{l.type.replace("_", " ")}</td>
                        <td className={`p-3 font-semibold ${l.change >= 0 ? "text-brand-orange" : "text-red-600"}`}>{l.change >= 0 ? `+${l.change}` : l.change}</td>
                        <td className="p-3 text-brand-charcoal">{l.resulting_stock}</td>
                        <td className="p-3 text-brand-stone">{l.reason || "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
